import { Helmet } from 'react-helmet';
import { useTranslation } from '../i18n/LanguageContext';
import { SITE_NAME, SITE_URLS } from '../lib/seo/constants';

function buildJsonLdGraph(schemas) {
  const filtered = schemas.filter(Boolean);
  if (filtered.length === 0) return null;
  if (filtered.length === 1) return filtered[0];
  return {
    '@context': 'https://schema.org',
    '@graph': filtered,
  };
}

export default function SEO({
  title,
  description,
  path = '/',
  ogImage,
  noindex = false,
  jsonLd,
}) {
  const { lang, t } = useTranslation();

  const siteUrl = SITE_URLS[lang] || SITE_URLS.en;
  const defaultOgImage = `${siteUrl}/img/og-image.png`;
  const resolvedDescription = description || t('seo.defaultDescription');
  const resolvedOgImage = ogImage || defaultOgImage;

  const pageTitle = title ? `${title} | ${SITE_NAME}` : t('seo.defaultTitle');
  const normalizedPath = path === '/' ? '/' : path.startsWith('/') ? path : `/${path}`;
  const canonicalUrl = normalizedPath === '/' ? `${siteUrl}/` : `${siteUrl}${normalizedPath}`;
  const alternateLang = lang === 'fr' ? 'en' : 'fr';
  const alternateSiteUrl = SITE_URLS[alternateLang];
  const alternateUrl =
    normalizedPath === '/' ? `${alternateSiteUrl}/` : `${alternateSiteUrl}${normalizedPath}`;
  const ogLocale = lang === 'fr' ? 'fr_FR' : 'en_US';
  const alternateOgLocale = lang === 'fr' ? 'en_US' : 'fr_FR';

  const organizationJsonLd = {
    '@type': 'Organization',
    name: SITE_NAME,
    url: `${siteUrl}/`,
    logo: defaultOgImage,
    sameAs: ['https://support.bibliona.com/'],
  };

  const websiteJsonLd = {
    '@type': 'WebSite',
    name: SITE_NAME,
    url: `${siteUrl}/`,
    inLanguage: lang,
    publisher: { '@type': 'Organization', name: SITE_NAME },
  };

  const pageSchemas = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];
  const graph = buildJsonLdGraph([organizationJsonLd, websiteJsonLd, ...pageSchemas]);

  return (
    <Helmet>
      <html lang={lang} />
      <title>{pageTitle}</title>
      <meta name="description" content={resolvedDescription} />
      <link rel="canonical" href={canonicalUrl} />
      <link rel="alternate" hrefLang={lang} href={canonicalUrl} />
      <link rel="alternate" hrefLang={alternateLang} href={alternateUrl} />
      <link rel="alternate" hrefLang="x-default" href={`${SITE_URLS.en}${normalizedPath === '/' ? '/' : normalizedPath}`} />

      {noindex && <meta name="robots" content="noindex, nofollow" />}

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={resolvedDescription} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={resolvedOgImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content={ogLocale} />
      <meta property="og:locale:alternate" content={alternateOgLocale} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={resolvedDescription} />
      <meta name="twitter:image" content={resolvedOgImage} />

      {graph && (
        <script type="application/ld+json">{JSON.stringify(graph)}</script>
      )}
    </Helmet>
  );
}
